import { AlertColor, Box, Card, Grid } from '@mui/material';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TitleText from '../../components/formlib/TitleText';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import MediumTypography from '../../components/formlib/MediumTypography';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { useLocation, useNavigate } from 'react-router';
import ProgramPanel from '../../components/forms/ProgramPanel';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import { BATELLE_DEVELOPMENT_INVENTORY_2 } from '../../routes/Routing';
import { RiskFactorType } from '../../utils/type';
import Textinput from '../../components/formlib/Textinput';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import CustomRadioButton from '../../components/formlib/CustomRadioButton';
import {
  addOrUpdateRiskFactor,
  getRiskFactorData,
} from '../../services/configApi/riskFactor/riskFactor';
import ModalPopup from '../../components/formlib/ModalPopup';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { debounce } from 'lodash';
import { EICSRadioData } from '../intake/IntakeClientsInfo';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';

const initialValues: RiskFactorType = {
  parent: {
    name: '',
    contactType: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
  },
  client: {
    name: '',
    sex: '',
    dphId: '',
    dateOfBirth: '',
    referralBeginDate: '',
    dateOfEvaluation: '',
    intakeFacilitator: '',
    serviceCoordinator: {
      id: '',
      name: '',
    },
  },
  childCharacteristics: {
    gestationalAge: '',
    childInNICU: '',
    daysInNICU: '',
    birthWeight: '',
    weightInLbs: '',
    weightInOz: '',
    weightInGrams: '',
    childHospitalized: '',
    childDiagnosedAtBirth: '',
    smallSize: '',
    childChronicFeeding: '',
    childBloodLevel: '',
    childInsecureDifficulties: '',
    childNervousSystem: '',
    childMultipleTrauma: '',
    childDiagnosis: '',
  },
  familyCharacteristics: {
    birthMotherParity: '',
    mothersEducationYear: '',
    parentalChronicIllness: '',
    socialSupport: '',
    lackingAdequate: '',
    protectiveServiceInvestigation: '',
    affectingChild: '',
    abuseAffectingChild: '',
    homelessness: '',
    homelessnessInPast: '',
    siblingWithSEN: '',
  },
};

const validationSchema = yup.object().shape({
  childCharacteristics: yup.object().shape({
    weightInLbs: yup.string().when('birthWeight', {
      is: 'In Pounds',
      then: yup.string().required('Lbs field is required'),
      otherwise: yup.string(),
    }),
    weightInOz: yup.string().when('birthWeight', {
      is: 'In Pounds',
      then: yup.string().required('Oz field is required'),
      otherwise: yup.string(),
    }),
    weightInGrams: yup.string().when('birthWeight', {
      is: 'In Grams',
      then: yup.string().required('Grams field is required'),
      otherwise: yup.string(),
    }),
  }),
});

const RiskFactor = () => {
  const location = useLocation();
  const [softValidation, setSoftValidation] = useState<boolean>(false);
  const [evaluationId] = useState<string>(location.state?.evaluationId);
  const [evaluationType] = useState<string>(location.state?.evaluationType);
  const [uploadedToTBR] = useState<boolean>(location.state?.isUploadedToTBR);
  const formikRef = useRef<FormikProps<RiskFactorType>>(null);
  const [riskFactorData, setRiskFactorData] = useState<RiskFactorType | null>(
    null,
  );
  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { hasUnsavedChanges, handleUpdateUnsavedChanges } = useContext(
    UnSavedChangesContext,
  );
  const [gestationalAgeUnknown, setGestationalAgeUnknown] = useState(false);
  const [NICUDaysUnknown, setNICUDaysUnknown] = useState(false);
  const [birthWeightPoundsUnknown, setBirthWeightPoundsUnknown] =
    useState(false);
  const [birthWeightOuncesUnknown, setBirthWeightOuncesUnknown] =
    useState(false);
  const [gramsUnknown, setGramsUnknown] = useState(false);
  const [userModified, setUserModified] = useState(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [
    birthMotherEducationYearsUnknown,
    setBirthMotherEducationYearsUnknown,
  ] = useState(false);
  const [riskFactorInitialValues] = useState<RiskFactorType>(initialValues);
  const navigate = useNavigate();
  const clientID = localStorage.getItem('ClientId');
  const [isNICUSelected, setIsNICUSelected] = useState<boolean>(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  const hasEvalAdd = checkPermissionForFeature(
    'backend.evaluations',
    'addPermission',
  );
  const hasEvalEdit = checkPermissionForFeature(
    'backend.evaluations',
    'editPermission',
  );
  const hasId = !!riskFactorData?.id;

  const isScreenLocked = useMemo(() => {
    return (
      !hasEditPermission || (!hasId && !hasEvalAdd) || (hasId && !hasEvalEdit)
    );
  }, [hasEditPermission, hasEvalAdd, hasEvalEdit, hasId]);

  React.useEffect(() => {
    if (isScreenLocked) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isScreenLocked]);

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  useEffect(() => {
    toggleLoader(true);
    riskFactorGetFunction();
  }, []);

  useEffect(() => {
    if (userModified) {
      handleUpdateUnsavedChanges(true);
    } else {
      handleUpdateUnsavedChanges(false);
    }
  }, [userModified]);

  const riskFactorGetFunction = () => {
    getRiskFactorData(
      evaluationId,
      localStorage.getItem('ClientId'),
      evaluationType,
    )
      .then((data) => {
        toggleLoader(false);
        setRiskFactorData(data);
        if (formikRef.current) {
          formikRef.current.setValues({
            ...data,
          });
        }
        setIsNICUSelected(data.childCharacteristics.childInNICU === 'Yes');
        setGestationalAgeUnknown(
          data.childCharacteristics.gestationalAge == 'Unknown',
        );
        setNICUDaysUnknown(data.childCharacteristics.daysInNICU == 'Unknown');
        setBirthMotherEducationYearsUnknown(
          data.familyCharacteristics.mothersEducationYear == 'Unknown',
        );
        setBirthWeightPoundsUnknown(
          data.childCharacteristics.weightInLbs == 'Unknown',
        );
        setBirthWeightOuncesUnknown(
          data.childCharacteristics.weightInOz == 'Unknown',
        );
        setGramsUnknown(data.childCharacteristics.weightInGrams == 'Unknown');
      })
      .catch((error) => {
        toggleLoader(false);
        setOpenSnackBar(true);
        setToastrVariable('error');
        setToastrId('failed');
        setToastrDefaultMessage(error);
      });
  };

  const allFieldsEnteredCheck = (values: RiskFactorType) => {
    if (
      values.childCharacteristics.gestationalAge === '' ||
      values.childCharacteristics.childInNICU === '' ||
      values.childCharacteristics.childHospitalized === '' ||
      values.childCharacteristics.childDiagnosedAtBirth === '' ||
      values.childCharacteristics.smallSize === '' ||
      values.childCharacteristics.childChronicFeeding === '' ||
      values.childCharacteristics.childBloodLevel === '' ||
      values.childCharacteristics.childInsecureDifficulties === '' ||
      values.childCharacteristics.childNervousSystem === '' ||
      values.childCharacteristics.childMultipleTrauma === '' ||
      values.childCharacteristics.childDiagnosis === '' ||
      values.familyCharacteristics.siblingWithSEN === '' ||
      values.familyCharacteristics.birthMotherParity === '' ||
      values.familyCharacteristics.mothersEducationYear === '' ||
      values.familyCharacteristics.parentalChronicIllness === '' ||
      values.familyCharacteristics.socialSupport === '' ||
      values.familyCharacteristics.lackingAdequate === '' ||
      values.familyCharacteristics.protectiveServiceInvestigation === '' ||
      values.familyCharacteristics.affectingChild === '' ||
      values.familyCharacteristics.abuseAffectingChild === '' ||
      values.familyCharacteristics.homelessness === '' ||
      values.familyCharacteristics.homelessnessInPast === '' ||
      values.childCharacteristics.birthWeight === '' ||
      (values.childCharacteristics.birthWeight === 'Pounds' &&
        (values.childCharacteristics.weightInLbs === '' ||
          values.childCharacteristics.weightInOz === '')) ||
      (values.childCharacteristics.birthWeight === 'Grams' &&
        values.childCharacteristics.weightInGrams === '')
    ) {
      return false;
    }
    return true;
  };

  const saveDebounce = useCallback(
    debounce((riskfactorData) => {
      addOrUpdateRiskFactor(riskfactorData, clientID)
        .then(async (response) => {
          toggleLoader(false);
          setSoftValidation(false);
          setUserModified(false);
          riskFactorGetFunction();
          setOpenSnackBar(true);
          setToastrId(response.message);
          setToastrDefaultMessage(response.message);
          setToastrVariable('success');
        })
        .catch(async (error) => {
          toggleLoader(false);
          setSoftValidation(false);
          setOpenSnackBar(true);
          setToastrId(error.message);
          setToastrDefaultMessage(error.message);
          setToastrVariable('error');
        });
    }, 500),
    [],
  );

  const handleSaveRiskFactor = (values: RiskFactorType) => {
    const riskfactorData = {
      id: riskFactorData?.id ? riskFactorData?.id : '',
      evaluationId: evaluationId,
      evaluationType: evaluationType,
      client: values.client,
      parent: values.parent,
      childCharacteristics: values.childCharacteristics,
      familyCharacteristics: values.familyCharacteristics,
      completed: allFieldsEnteredCheck(values),
    };
    if (riskfactorData.completed) {
      toggleLoader(true);
      saveDebounce(riskfactorData);
    } else {
      setSoftValidation(true);
    }
  };

  return (
    <Box component="main">
      <Box
        ref={parentRef}
        style={{
          pointerEvents: isScreenLocked ? 'none' : 'auto',
          opacity: isScreenLocked ? '0.8' : '1 ',
        }}
      >
        <SnackBarComponent
          open={openSnackBar}
          handleClose={handleCloseSnakBar}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        <Box component="section">
          <Box component="div" className="rowContainer">
            <CustomBackArrow
              onClick={() => {
                if (hasUnsavedChanges) {
                  setOpenUnSavedModal(true);
                } else {
                  handleUpdateUnsavedChanges(false);
                  navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
                }
              }}
            />
            <Grid container>
              <Grid item xs={6} lg={6}>
                <Box className="flex__  align__items__center">
                  <TitleText
                    labelid="RiskFactor.TitleText"
                    defaultlabel="Risk Factor Evaluation"
                    Sxprops={{ ml: 1 }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={6}
                lg={6}
                className="flex__ align__items__center justifyContent-FlexEnd"
              ></Grid>
            </Grid>
          </Box>
          <Box component="div">
            <Formik
              initialValues={riskFactorInitialValues}
              validateOnMount={true}
              innerRef={formikRef}
              validateOnChange={true}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values) => {
                handleSaveRiskFactor(values);
              }}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Card className="formCardview">
                  {riskFactorData && (
                    <ProgramPanel
                      program={riskFactorData.program}
                      client={{
                        id: 0,
                        clientId: '',
                        name: riskFactorData.client.name,
                        firstName: '',
                        lastName: '',
                        sex: riskFactorData.client.sex,
                        dob: riskFactorData.client.dateOfBirth,
                        dphId: riskFactorData.client.dphId,
                        referralBeginDate:
                          riskFactorData.client.referralBeginDate,
                        dateOfEvaluation:
                          riskFactorData.client.dateOfEvaluation,
                        serviceCoordinatorId: '',
                        intakeFacilitatorId: '',
                        serviceCoordinatorName:
                          riskFactorData.client.serviceCoordinator.name,
                        serviceCoordinatorPhone: '',
                        serviceCoordinatorPhoneExtension: '',
                        primarySpokenLanguage: '',
                        primaryWrittenLanguage: '',
                        race: '',
                        ethnicity: '',
                        pediatricianName: '',
                        pediatricianPhoneNumber: '',
                      }}
                      parents={[
                        {
                          contactId: '',
                          name: riskFactorData.parent.name,
                          homePhoneNumber: riskFactorData.parent.phone,
                          isPrimary: false,
                          type: '',
                          relationshipToClient:
                            riskFactorData.parent.relationship,
                          email: riskFactorData.parent.email,
                          address: riskFactorData.parent.address,
                        },
                      ]}
                    />
                  )}
                  <Box>
                    <Grid container className="mt-md">
                      <Grid
                        item
                        xs={12}
                        lg={12}
                        sx={{
                          border: '1px solid #00C6B8',
                          background: '#ECF9F8',
                        }}
                      >
                        <Box className="p-sm">
                          <MediumTypography
                            labelid={'RiskFactor.ChildCharacteristics'}
                            defaultlabel="Child Characteristics"
                            fontSize="18px"
                            fontweight={600}
                            textColor="#2a4241"
                          />
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '12px' }}
                          />
                          <Box className="rowContainer">
                            <Grid container>
                              <Grid item xs={3.5} lg={3.5}>
                                <MediumTypography
                                  className="mb-sm"
                                  labelid="RiskFactor.PregnancyLong"
                                  defaultlabel="How many weeks long was the pregnancy?"
                                />
                                <Box className="mb-md">
                                  <Textinput
                                    name="gestationalAge"
                                    labelid={'RiskFactor.GestationalAge'}
                                    defaultlabelid="Gestational Age (in weeks)"
                                    Required={false}
                                    inputProps={{
                                      maxLength: 2,
                                    }}
                                    sxProps={{ background: 'white' }}
                                    handlechange={(value) => {
                                      setUserModified(true);
                                      const numericValue = value.replaceAll(
                                        /\D/g,
                                        '',
                                      );
                                      setFieldValue(
                                        'childCharacteristics.gestationalAge',
                                        numericValue,
                                      );
                                    }}
                                    type="text"
                                    Value={
                                      values.childCharacteristics.gestationalAge
                                    }
                                    disabled={
                                      gestationalAgeUnknown ||
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                  />
                                  <CheckBoxComponent
                                    disabled={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    defaultlabelid="Unknown"
                                    labelid="RiskFactor.Unknown"
                                    ischecked={gestationalAgeUnknown}
                                    CheckHandleChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      setGestationalAgeUnknown(
                                        e.target.checked,
                                      );
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.gestationalAge',
                                        e.target.checked ? 'Unknown' : '',
                                      );
                                    }}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={8.5} lg={8.5}></Grid>
                              <Grid item xs={3.5} lg={3.5}>
                                <DropdownComponent
                                  disabled={
                                    evaluationType == 'INITIALEVALUATION' ||
                                    uploadedToTBR ||
                                    isScreenLocked
                                  }
                                  name="nicuStay"
                                  names={[
                                    {
                                      id: 'Yes',
                                      label: 'Yes',
                                    },
                                    {
                                      id: 'No',
                                      label: 'No',
                                    },
                                    {
                                      id: 'Unknown',
                                      label: 'Unknown',
                                    },
                                  ]}
                                  labelid="RiskFactor.NICUStay"
                                  handleChange={(e) => {
                                    setUserModified(true);
                                    setFieldValue(
                                      'childCharacteristics.childInNICU',
                                      e,
                                    );
                                    setIsNICUSelected(e === 'Yes');
                                  }}
                                  defaultlabelid="NICU Stay"
                                  value={
                                    values.childCharacteristics.childInNICU
                                  }
                                  placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                                />
                              </Grid>
                              <Grid item xs={0.5} lg={0.5}></Grid>
                              {values.childCharacteristics.childInNICU ===
                                'Yes' && (
                                <Grid item xs={3.5} lg={3.5}>
                                  <Textinput
                                    name="daysInNICU"
                                    labelid={'RiskFactor.NICUDays'}
                                    defaultlabelid="NICU Days"
                                    Required={false}
                                    inputProps={{
                                      maxLength: 3,
                                    }}
                                    sxProps={{ background: 'white' }}
                                    handlechange={(value) => {
                                      setUserModified(true);
                                      const numericValue = value.replaceAll(
                                        /\D/g,
                                        '',
                                      );
                                      setFieldValue(
                                        'childCharacteristics.daysInNICU',
                                        numericValue,
                                      );
                                    }}
                                    type="text"
                                    Value={
                                      values.childCharacteristics.daysInNICU
                                    }
                                    disabled={
                                      NICUDaysUnknown ||
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                  />
                                  {isNICUSelected &&
                                    values.childCharacteristics.daysInNICU ===
                                      '' && (
                                      <MediumTypography
                                        className="errorText-md"
                                        labelid="nicuFieldRequiredMessage"
                                        defaultlabel="NICU Days is required"
                                      />
                                    )}
                                  <CheckBoxComponent
                                    disabled={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    defaultlabelid="Unknown"
                                    labelid="RiskFactor.Unknown"
                                    ischecked={NICUDaysUnknown}
                                    CheckHandleChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      setNICUDaysUnknown(e.target.checked);
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.daysInNICU',
                                        e.target.checked ? 'Unknown' : '',
                                      );
                                    }}
                                  />
                                </Grid>
                              )}
                              <Grid
                                container
                                rowSpacing={'24px'}
                                className="pt-md align__items__center"
                              >
                                <Box className="rowContainer">
                                  <Grid>
                                    <MediumTypography
                                      labelid="RiskFactor.BirthWeight"
                                      defaultlabel={`How much did the child weigh at birth?`}
                                      fontSize="14px"
                                    />
                                  </Grid>
                                  <Grid className="ml-sm">
                                    <CustomRadioButton
                                      disable={
                                        evaluationType == 'INITIALEVALUATION' ||
                                        uploadedToTBR
                                      }
                                      data={[
                                        {
                                          value: 'In Pounds',
                                          labelId: 'RiskFactor.Pounds',
                                          defaultLabel: 'In Pounds',
                                        },
                                        {
                                          value: 'In Grams',
                                          labelId: 'RiskFactor.Grams',
                                          defaultLabel: 'In Grams',
                                        },
                                        {
                                          value: 'Unknown',
                                          labelId: 'Unknown',
                                          defaultLabel: 'Unknown',
                                        },
                                      ]}
                                      value={
                                        values.childCharacteristics.birthWeight
                                      }
                                      onSelectItem={(value) => {
                                        setUserModified(true);
                                        setFieldValue(
                                          'childCharacteristics.weightInGrams',
                                          '',
                                        );
                                        setGramsUnknown(false);
                                        setFieldValue(
                                          'childCharacteristics.weightInLbs',
                                          '',
                                        );
                                        setBirthWeightOuncesUnknown(false);
                                        setFieldValue(
                                          'childCharacteristics.weightInOz',
                                          '',
                                        );
                                        setBirthWeightPoundsUnknown(false);
                                        setFieldValue(
                                          'childCharacteristics.birthWeight',
                                          value,
                                        );
                                      }}
                                      row={false}
                                    />
                                  </Grid>
                                </Box>
                              </Grid>
                              <Grid container>
                                {values.childCharacteristics.birthWeight ==
                                  'In Pounds' && (
                                  <Grid container className="mb-md">
                                    <Grid item xs={5} lg={5}>
                                      <Textinput
                                        name="weightInLbs"
                                        labelid={'RiskFactor.BirthWeightPounds'}
                                        defaultlabelid="Lbs"
                                        Required={true}
                                        inputProps={{
                                          maxLength: 2,
                                        }}
                                        sxProps={{ background: 'white' }}
                                        handlechange={(value) => {
                                          setUserModified(true);
                                          const numericValue = value.replaceAll(
                                            /\D/g,
                                            '',
                                          );
                                          setFieldValue(
                                            'childCharacteristics.weightInLbs',
                                            numericValue,
                                          );
                                        }}
                                        type="text"
                                        Value={
                                          values.childCharacteristics
                                            .weightInLbs
                                        }
                                        disabled={
                                          birthWeightPoundsUnknown ||
                                          evaluationType ==
                                            'INITIALEVALUATION' ||
                                          uploadedToTBR
                                        }
                                      />
                                      {errors.childCharacteristics
                                        ?.weightInLbs &&
                                        touched.childCharacteristics
                                          ?.weightInLbs && (
                                          <MediumTypography
                                            labelid={
                                              errors.childCharacteristics
                                                .weightInLbs
                                            }
                                            defaultlabel={
                                              errors.childCharacteristics
                                                .weightInLbs
                                            }
                                            className="errorText-md"
                                          />
                                        )}
                                    </Grid>
                                    <Grid item xs={0.5} lg={0.5}></Grid>
                                    <Grid item xs={5} lg={5}>
                                      <Textinput
                                        name="weightInOz"
                                        labelid={'RiskFactor.BirthWeightOunces'}
                                        defaultlabelid="Oz"
                                        Required={true}
                                        inputProps={{
                                          maxLength: 5,
                                        }}
                                        sxProps={{ background: 'white' }}
                                        handlechange={(text: string) => {
                                          // Auto-prepend 0 if user starts with '.'
                                          if (text.startsWith('.')) {
                                            text = '0' + text;
                                            setFieldValue(
                                              'childCharacteristics.weightInOz',
                                              text.toString(),
                                            );
                                          }

                                          // Full match for up to 2 digits before and after dot
                                          const validDecimal = new RegExp(
                                            /^\d{1,2}(\.\d{0,2})?$/,
                                          ).exec(text);

                                          if (validDecimal) {
                                            const num = Number.parseFloat(
                                              text || '0',
                                            );

                                            if (num >= 0 && num <= 15) {
                                              setFieldValue(
                                                'childCharacteristics.weightInOz',
                                                text,
                                              );
                                            } else {
                                              setFieldValue(
                                                'childCharacteristics.weightInOz',
                                                '',
                                              );
                                            }
                                          } else {
                                            // Sanitize: remove non-digit/dot, limit digits & 1 dot
                                            const cleaned = text
                                              .replaceAll(/[^\d.]/g, '') // remove invalid chars
                                              .replace(
                                                /^(\d{0,2})(\.\d{0,2})?.*$/,
                                                '$1$2',
                                              ); // limit format

                                            setFieldValue(
                                              'childCharacteristics.weightInOz',
                                              cleaned,
                                            );
                                          }
                                        }}
                                        onblur={() => {
                                          if (
                                            values.childCharacteristics
                                              .weightInOz
                                          ) {
                                            setFieldValue(
                                              'childCharacteristics.weightInOz',
                                              values.childCharacteristics.weightInOz.startsWith(
                                                '.',
                                              )
                                                ? '0.00'
                                                : Number.parseFloat(
                                                    values.childCharacteristics
                                                      .weightInOz,
                                                  ).toFixed(2),
                                            );
                                          }
                                        }}
                                        Value={
                                          values.childCharacteristics.weightInOz
                                        }
                                        disabled={
                                          birthWeightOuncesUnknown ||
                                          evaluationType ==
                                            'INITIALEVALUATION' ||
                                          uploadedToTBR
                                        }
                                      />
                                      {errors.childCharacteristics
                                        ?.weightInOz &&
                                        touched.childCharacteristics
                                          ?.weightInOz && (
                                          <MediumTypography
                                            labelid={
                                              errors.childCharacteristics
                                                .weightInOz
                                            }
                                            defaultlabel={
                                              errors.childCharacteristics
                                                .weightInOz
                                            }
                                            className="errorText-md"
                                          />
                                        )}
                                    </Grid>
                                  </Grid>
                                )}

                                {values.childCharacteristics.birthWeight ==
                                  'In Grams' && (
                                  <Grid container className="mb-md">
                                    <Grid item xs={5} lg={5}>
                                      <Textinput
                                        name="weightInLbs"
                                        labelid={'RiskFactor.BirthWeightGrams'}
                                        defaultlabelid="Grams"
                                        Required={true}
                                        inputProps={{
                                          maxLength: 4,
                                        }}
                                        sxProps={{ background: 'white' }}
                                        handlechange={(value) => {
                                          setUserModified(true);
                                          const numericValue = value.replaceAll(
                                            /\D/g,
                                            '',
                                          );
                                          setFieldValue(
                                            'childCharacteristics.weightInGrams',
                                            numericValue,
                                          );
                                        }}
                                        type="text"
                                        Value={
                                          values.childCharacteristics
                                            .weightInGrams
                                        }
                                        disabled={
                                          gramsUnknown ||
                                          evaluationType ==
                                            'INITIALEVALUATION' ||
                                          uploadedToTBR
                                        }
                                      />
                                      {errors.childCharacteristics
                                        ?.weightInGrams &&
                                        touched.childCharacteristics
                                          ?.weightInGrams && (
                                          <MediumTypography
                                            labelid={
                                              errors.childCharacteristics
                                                .weightInGrams
                                            }
                                            defaultlabel={
                                              errors.childCharacteristics
                                                .weightInGrams
                                            }
                                            className="errorText-md"
                                          />
                                        )}
                                    </Grid>
                                  </Grid>
                                )}
                              </Grid>
                              <Grid
                                container
                                className="pb-md align__items__center"
                              >
                                <Grid item xs={7} lg={7}>
                                  {/* <Tooltip
                                      id="tool-tip"
                                      title={
                                        'Was the child hospitalized for more than 25 days during a 6-month period, excluding the initial birth admission to a NICU ?'
                                      }
                                      placement="top"
                                    > */}
                                  <Box>
                                    <MediumTypography
                                      labelid="IntakePregnancy.childHospitalized"
                                      defaultlabel={`Was the child hospitalized for more than 25 days during a 6-month period, excluding the initial birth admission to a NICU ?`}
                                      fontSize="14px"
                                      // sxProps={{ ...ellipsisCellSX }}
                                    />
                                  </Box>
                                  {/* </Tooltip> */}
                                </Grid>
                                <Grid item xs={1} lg={1}></Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childHospitalized
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.childHospitalized',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={7} lg={7}>
                                  {/* <Tooltip
                                      id="tool-tip"
                                      title={
                                        'Was Child Diagnosed at Birth with Intrauterine Growth Restriction(IUGR) or Small for the Gestational Age (SGA) ?'
                                      }
                                      placement="top"
                                    > */}
                                  <Box>
                                    <MediumTypography
                                      labelid="RiskFactor.IURRORSGA"
                                      defaultlabel={`Was Child Diagnosed at Birth with Intrauterine Growth Restriction(IUGR) or Small for the Gestational Age (SGA) ?`}
                                      fontSize="14px"
                                      // sxProps={{ ...ellipsisCellSX }}
                                    />
                                  </Box>
                                  {/* </Tooltip> */}
                                </Grid>
                                <Grid item xs={1} lg={1}></Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childDiagnosedAtBirth
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);

                                      setFieldValue(
                                        'childCharacteristics.childDiagnosedAtBirth',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.SmallSize"
                                    defaultlabel={`Is child's weight, weight for height, or height for age less than the 5th percentile (Small size) ?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics.smallSize
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);

                                      setFieldValue(
                                        'childCharacteristics.smallSize',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.FeedingDifficulty"
                                    defaultlabel={`Chronic Feeding Difficulties?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childChronicFeeding
                                    }
                                    onSelectItem={(value) => {
                                      setFieldValue(
                                        'childCharacteristics.childChronicFeeding',
                                        value,
                                      );
                                      setUserModified(true);
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.ElevatedLeadLevels"
                                    defaultlabel={`Blood Lead Levels Measured 5 Micrograms/dl ?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childBloodLevel
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.childBloodLevel',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.AttachmentDifficulty"
                                    defaultlabel={`Difficulty with insecure attachment/interactional difficulties?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childInsecureDifficulties
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.childInsecureDifficulties',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.CNSAbnormality"
                                    defaultlabel={`Suspected Central Nervous System Abnormality?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childNervousSystem
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.childNervousSystem',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.MultipleTraumas"
                                    defaultlabel={`Multiple trauma/losses experienced by child?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics
                                        .childMultipleTrauma
                                    }
                                    onSelectItem={(value) => {
                                      setFieldValue(
                                        'childCharacteristics.childMultipleTrauma',
                                        value,
                                      );
                                      setUserModified(true);
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />{' '}
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.SubstanceExposed"
                                    defaultlabel={`Diagnosis of Substance Exposed Newborn (SEN)?`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.childCharacteristics.childDiagnosis
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'childCharacteristics.childDiagnosis',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                              <Box
                                className="horizontalLine"
                                sx={{ marginY: '0px' }}
                              />
                              <Grid
                                container
                                className="pt-md pb-md align__items__center"
                              >
                                <Grid item xs={8} lg={8}>
                                  <MediumTypography
                                    labelid="RiskFactor.SENorNASSibling"
                                    defaultlabel={`Sibling with a diagnosis of Substance Exposed Newborn (SEN)`}
                                    fontSize="14px"
                                  />
                                </Grid>
                                <Grid item xs={3} lg={3}>
                                  <CustomRadioButton
                                    disable={
                                      evaluationType == 'INITIALEVALUATION' ||
                                      uploadedToTBR
                                    }
                                    data={EICSRadioData}
                                    value={
                                      values.familyCharacteristics
                                        .siblingWithSEN
                                    }
                                    onSelectItem={(value) => {
                                      setUserModified(true);
                                      setFieldValue(
                                        'familyCharacteristics.siblingWithSEN',
                                        value,
                                      );
                                    }}
                                    row={false}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container className="mt-xlg">
                      <Grid
                        item
                        xs={12}
                        xl={12}
                        sx={{
                          border: '1px solid #00C6B8',
                          background: '#ECF9F8',
                        }}
                      >
                        <Box className="p-sm">
                          <MediumTypography
                            labelid={'RiskFactor.FamilyCharacteristics'}
                            defaultlabel="Family Characteristics"
                            fontSize="18px"
                            fontweight={600}
                            textColor="#2a4241"
                          />
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '12px' }}
                          />
                          <Grid container className="align__items__center">
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.BirthMotherParity"
                                defaultlabel={`Mother less than 17 years of age and/or with 3 or more children before 20 (Birth Mother Parity) ?`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics.birthMotherParity
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.birthMotherParity',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Grid item xs={5} lg={5} className="pt-sm">
                            {' '}
                            <Textinput
                              name="birthMotherEducationYears"
                              labelid={'RiskFactor.BirthMotherEducationYears'}
                              defaultlabelid="Birth Mother Education Years"
                              Required={false}
                              handlechange={(text: string) => {
                                const regex = /^\d+$/;
                                if (regex.test(text) || text === '') {
                                  setFieldValue(
                                    'familyCharacteristics.mothersEducationYear',
                                    text,
                                  );
                                }
                              }}
                              inputProps={{
                                maxLength: 2,
                              }}
                              sxProps={{ background: 'white' }}
                              Value={
                                values.familyCharacteristics
                                  .mothersEducationYear
                              }
                              disabled={
                                birthMotherEducationYearsUnknown ||
                                evaluationType == 'INITIALEVALUATION' ||
                                uploadedToTBR
                              }
                            />
                            <CheckBoxComponent
                              disabled={
                                evaluationType == 'INITIALEVALUATION' ||
                                uploadedToTBR
                              }
                              defaultlabelid="Unknown"
                              labelid="RiskFactor.Unknown"
                              ischecked={birthMotherEducationYearsUnknown}
                              CheckHandleChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setUserModified(true);
                                setBirthMotherEducationYearsUnknown(
                                  e.target.checked,
                                );

                                setFieldValue(
                                  'familyCharacteristics.mothersEducationYear',
                                  e.target.checked ? 'Unknown' : '',
                                );
                              }}
                            />
                          </Grid>
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.ParentalCondition"
                                defaultlabel={`Parental Chronic Illness or Disability that Affects Caregiving Ability
                                `}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics
                                    .parentalChronicIllness
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.parentalChronicIllness',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.LackingSupport"
                                defaultlabel={`Family lacking social supports?`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics.socialSupport
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.socialSupport',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.LackingGoods"
                                defaultlabel={`Lacking Adequate Food or Clothing`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics.lackingAdequate
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.lackingAdequate',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.ProtectiveServices"
                                defaultlabel={`Open/Confirmed Protective Service Investigation`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics
                                    .protectiveServiceInvestigation
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.protectiveServiceInvestigation',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.SubstanceAbuse"
                                defaultlabel={`Substance Abuse Affecting Child`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics.affectingChild
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.affectingChild',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.PhysicalorEmotionalAbuse"
                                defaultlabel={`Abuse (physical or emotional) affecting child?`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics
                                    .abuseAffectingChild
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.abuseAffectingChild',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.Homelessness"
                                defaultlabel={`Homelessness`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics.homelessness
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.homelessness',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />{' '}
                          <Grid
                            container
                            className="pt-md pb-md align__items__center"
                          >
                            <Grid item xs={8} lg={8}>
                              <MediumTypography
                                labelid="RiskFactor.HomelessLast12Months"
                                defaultlabel={` Homeless in the past 12 months`}
                                fontSize="14px"
                              />
                            </Grid>
                            <Grid item xs={3} lg={3}>
                              <CustomRadioButton
                                disable={
                                  evaluationType == 'INITIALEVALUATION' ||
                                  uploadedToTBR
                                }
                                data={EICSRadioData}
                                value={
                                  values.familyCharacteristics
                                    .homelessnessInPast
                                }
                                onSelectItem={(value) => {
                                  setUserModified(true);
                                  setFieldValue(
                                    'familyCharacteristics.homelessnessInPast',
                                    value,
                                  );
                                }}
                                row={false}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              )}
            </Formik>
          </Box>
        </Box>
        <Box>
          <Grid
            className="flex__ justifyContent-FlexEnd pt-md pb-lg "
            container
            direction="row"
            alignItems="right"
          >
            <Grid item>
              <ButtonComponent
                className="btn-primary btn-cancel mr-md"
                variantType="contained"
                defaultLabelId="Cancel"
                labelId="Contacts.cancelbtn"
                sxProps={{ display: isScreenLocked ? 'none' : 'auto' }}
                onClick={() => {
                  if (userModified) {
                    setCancelModal(true);
                  } else {
                    navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
                  }
                }}
              />

              <ButtonComponent
                className="btn-primary btn-submit"
                variantType="contained"
                type="submit"
                sxProps={{ display: isScreenLocked ? 'none' : 'auto' }}
                labelId={
                  riskFactorData?.id ? 'clientpage.Update' : 'Insurance.save'
                }
                defaultLabelId={riskFactorData?.id ? 'Update' : 'Save'}
                disabled={
                  evaluationType == 'INITIALEVALUATION' || uploadedToTBR
                }
                onClick={() => {
                  const values = formikRef.current?.values;
                  if (
                    isNICUSelected &&
                    values?.childCharacteristics.daysInNICU === ''
                  ) {
                    setIsNICUSelected(true);
                  } else {
                    formikRef.current?.handleSubmit();
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>
        {softValidation && (
          <ModalPopup
            open={softValidation}
            onCancel={() => {
              setSoftValidation(false);
            }}
            description="Intake.ReviewConfirmationSoft"
            onOk={() => {
              const riskfactorData = {
                id: riskFactorData?.id ? riskFactorData?.id : '',
                evaluationId: evaluationId,
                evaluationType: evaluationType,
                client: formikRef.current?.values.client,
                parent: formikRef.current?.values.parent,
                childCharacteristics:
                  formikRef.current?.values.childCharacteristics,
                familyCharacteristics:
                  formikRef.current?.values.familyCharacteristics,
                completed:
                  formikRef.current &&
                  allFieldsEnteredCheck(formikRef.current?.values),
              };
              toggleLoader(true);
              saveDebounce(riskfactorData);
            }}
            labelId1="Clientpage.Nobtn"
            negativeActionLabel="No"
            labelId2="Clientpage.Yesbtn"
            positiveActionLabel="Yes"
          />
        )}
        {cancelModal && (
          <ModalPopup
            open={cancelModal}
            onCancel={() => {
              setCancelModal(false);
            }}
            description="formUnsavedChangesMessage"
            onOk={() => {
              riskFactorGetFunction();
              setCancelModal(false);
              setUserModified(false);
            }}
            labelId1="Clientpage.cancelbtn"
            negativeActionLabel="cancelText"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="deleteText"
          />
        )}
        <ModalPopup
          open={openUnSavedModal}
          description="UnsavedAlertDescriptionWhileLeaving"
          labelId1="UnSavedAlertCancel"
          labelId2="UnSavedAlertLeave"
          negativeActionLabel="No"
          positiveActionLabel="Yes"
          onCancel={() => {
            setOpenUnSavedModal(false);
          }}
          onOk={() => {
            navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
            handleUpdateUnsavedChanges(false);
            setOpenUnSavedModal(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default RiskFactor;
